import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth/auth.service';
import { SessionHintService } from '../services/auth/session-hint.service';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { UserRole, parseUserRole } from '../../types/user-role';
import { homeUrlFor } from '../utils/user-role.util';

/**
 * Guard that redirects authenticated users away from auth pages (like login).
 * This enables cross-tab session synchronization - if user logs in on one tab,
 * refreshing another tab on the login page will redirect to the appropriate
 * landing page for their session type (customer dashboard or dev landing).
 *
 * Wired to the whole customer pre-auth GROUP as `canActivateChild` (the `/auth`
 * route, so it re-runs on EVERY child navigation — a plain `canActivate` on the
 * parent is skipped when moving between two /auth pages): login, forgot-password, reset-link, reset-password and
 * reset-password-status) and to the dev login (/dev-{slug}/login), so a
 * logged-in user on any of them is sent to the landing that matches their
 * actual session — not to the wrong area. Group-level is deliberate: the
 * login page's cross-tab reload only forwards a tab if the route it reloads
 * into is guarded, and it makes "Back to Login" from any reset page land on the
 * dashboard when a sibling tab has signed in. Only `LoginComponent` reloads on a
 * sibling sign-in; the four reset pages stay put until that link is clicked.
 *
 * Since the backend uses cookie-based authentication (shared across browser
 * tabs), this guard checks with the backend if there's an active session.
 */
export const redirectIfAuthenticatedGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const sessionHint = inject(SessionHintService);
  // ----------------------
  // Debug Log (Only enable when required to debug, do not allow this to land into prod)
  // console.log("[Redirect if Authenticated Guard] START.");
  // console.log("[Redirect if Authenticated Guard] Is Authenticated Flag: " + authService.isAuthenticated());
  // ----------------------
  // `role` picks the customer landing: a SuperAdmin has no Dashboard access and
  // goes to User Management (see `homeUrlFor`); every other role keeps the Dashboard.
  const redirectHome = (isDev: boolean | null | undefined, role: UserRole | null): false => {
    // ----------------------
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log("[Redirect if Authenticated Guard] Is Dev: " + isDev);
    // ----------------------
    if (isDev === true) {
      const slug = environment.devAreaSlug;
      if (slug)
        router.navigate([`/dev-${slug}`, 'logs', 'api-audit']);
    }
    else {
      router.navigateByUrl(homeUrlFor(role));
    }
    return false;
  };

  // Forced-logout short-circuit (AS-569).
  //
  // A forced logout — session superseded/expired, cross-tab logout, or
  // cross-scope login — navigates here via `AuthService.forceLogout()`, which
  // always attaches an `authReason` to the navigation state. That navigation is
  // AUTHORITATIVE: the server already invalidated this session (a 401 or the
  // `ForceLogout` SignalR push is the proof). We must NOT re-verify against the
  // backend in that case — its `/api/auth/is-authenticated` can still report the
  // just-superseded session as authenticated for a short window, which made this
  // guard bounce the user straight back to the dashboard and silently UNDO the
  // logout (the displaced device only logged out after a click). Let the forced
  // logout reach the login page untouched.
  const isForcedLogout = !!router.getCurrentNavigation()?.extras.state?.['authReason'];
  
  // ----------------------
  // Debug Log (Only enable when required to debug, do not allow this to land into prod)
  // console.log("[Redirect if Authenticated Guard] Forced Logout Flag: " + isForcedLogout);
  // console.log("[Redirect if Authenticated Guard] Auth Reason: " + router.getCurrentNavigation()?.extras.state?.['authReason']);
  // ----------------------
  if (isForcedLogout) {
    // ----------------------
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // [AS-569 debug] short-circuit trace — uncomment to re-enable. Dev only.
    // if (!environment.production) {
    //   console.log(
    //     '[Redirect if Authenticated Guard] forced-logout navigation (authReason present) → allowing /auth/login, skipping backend re-check'
    //   );
    // }
    // ----------------------
    return true;
  }

  // Fast path: if we already know the user is authenticated, redirect immediately
  if (authService.isAuthenticated() === true)
    return redirectHome(authService.isDev(), authService.role());

  // If auth state is unknown (null) or false, verify with backend
  // This handles the case where another tab logged in (cookies are shared)
  try {
    const response = await firstValueFrom(authService.ensureUserAuthenticated());
    // ----------------------
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // [AS-569 debug] is-authenticated re-check result — this is where the
    // superseded-but-still-"true" answer used to bounce a forced logout (now
    // fixed both via the short-circuit above and the backend making this
    // endpoint authoritative). Uncomment to re-enable. Dev only.
    // if (!environment.production) {
    //   console.log('[Redirect if Authenticated Guard] /auth/login re-check: is-authenticated result', {
    //     isAuthenticated: response.isAuthenticated,
    //     isDev: response.isDev,
    //     timestamp: new Date().toISOString(),
    //   });
    // }
    // ----------------------
    if (response.isAuthenticated) {
      // User is authenticated (possibly logged in from another tab).
      // CSRF only matters for cookie-auth customer sessions; dev sessions
      // use Bearer auth and CSRF would 401 noisily.
      if (response.isDev !== true)
        await firstValueFrom(authService.getCsrfToken());
      // ----------------------
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // [AS-569 debug] "redirecting away" trace — uncomment to re-enable. Dev only.
      // if (!environment.production) {
      //   console.warn(
      //     '[Redirect if Authenticated Guard] backend reports AUTHENTICATED → redirecting away from /auth/login. ' +
      //       'If this fires right after a ForceLogout, it is UNDOING the logout.',
      //     { isDev: response.isDev, timestamp: new Date().toISOString() }
      //   );
      // }
      // ----------------------
      return redirectHome(response.isDev, parseUserRole(response.role));
    }
    // Signed out on a direct visit to a login-side page: if a session lapsed
    // unseen in this browser, the login page toasts MBRS-AUTH once.
    sessionHint.stageNoticeIfLapsed();
  }
  catch (error) {
    // ----------------------
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // If backend check fails, allow access to login page
    // [AS-569 debug] csrf-401 trace — uncomment to re-enable. Dev only.
    // if (!environment.production) {
    //   console.warn(
    //     '[Redirect if Authenticated Guard] auth re-check threw (likely csrf-token 401) → allowing /auth/login',
    //     error
    //   );
    // }
    // ----------------------
    console.error('Error checking authentication status:', error);
  }
  // ----------------------
  // Debug Log (Only enable when required to debug, do not allow this to land into prod)
  // User is not authenticated, allow access to login page
  // [AS-569 debug] allow-access trace — uncomment to re-enable. Dev only.
  // if (!environment.production) {
  //   console.log('[Redirect if Authenticated Guard] allowing access to /auth/login (not authenticated)');
  // }
  // ----------------------
  return true;
};
